import { type Contact, type ContactGroup, type Group, TFN_LENGTH } from "@/shared-types";

/** check the transfer list and return a transfer option if a match is found for given number */
export function tryGetByVdnNumber(vdn: string, contacts: Contact[]) {
    // Don't include 555 (internal) vdn prefix
    if (vdn.startsWith("555")) {
        return contacts.find((option) => option.vdn === vdn.slice(3));
    }
    return contacts.find((option) => option.vdn === vdn);
}
export function tryGetByVdnDestination(destination: string, contacts: Contact[]) {
    return contacts.find((option) => option.number.toString() === destination);
}

export function getVdn(num: string, contacts: Contact[]) {
    let key = extractNumbers(num);
    if (key.length >= 7) {
        key = key.slice(-7); // VDN's are 7 digits appended to name
    }
    return tryGetByVdnNumber(key, contacts);
}

export function isInternalTfn(num: string, contacts: Contact[]) {
    let key = extractNumbers(num);
    if (key.length >= TFN_LENGTH) {
        key = key.slice(-TFN_LENGTH); // TFN's are 10 digits appended to name
    }
    return tryGetByVdnNumber(key, contacts)?.isInternal;
}

export function extractNumbers(value: string): string {
    return value.replace(/\D/g, "");
}

export function tryGetByVdnName(rawInput: string, contacts: Contact[]): Contact | undefined {
    return contacts.find((option) => option.name && option.name.toLowerCase() === rawInput.toLowerCase());
}

export function isValidVdn(vdn: string) {
    return /(?:\d{3})-(?:\d{4})/.test(vdn) || /^\d{7}$/.test(vdn) || /^\d{10}$/.test(vdn);
}

/** Returns a masked version of a TFN to use as a name
 * this utility could use work to not be dependent on a good string being passed in
 */
export const tfnToMaskedName = (tfn: string | null | undefined) => {
    const safeTfn = (tfn ?? "XXX").toString();
    const lastThreeDigits = safeTfn.slice(-3);
    return `+1(XXX) XXX-X${lastThreeDigits}`;
};

/** Returns a formatted version of contacts specifically to use
 * in Mantine's Autocomplete component. Will return [] if transfer list is disabled in appconfig
 */
export function groupContacts(contacts: Contact[]): ContactGroup[] {
    const filteredContacts = contacts.filter(
        (contact): contact is Contact & { group: Group } => contact.group !== undefined && contact.group !== "Unlisted",
    );

    const groupedContactsMap = filteredContacts.reduce((index, { group, name }) => {
        // If the group is exactly the same as the name, substitute with "Other".
        const groupKey = group === name ? "Other" : group;

        if (!index.has(groupKey)) {
            index.set(groupKey, []);
        }

        index.get(groupKey)?.push(name);
        return index;
    }, new Map<Group, string[]>());

    // Sort the names alphabetically.
    return Array.from(groupedContactsMap, ([group, items]) => ({
        group,
        items: items.toSorted(),
    }));
}
